/* eslint-disable quotes */
import dayjs from 'dayjs'
import { Craft } from '../types/Project/Craft'
import { ProjectViewModel } from '../types/Project/ProjectViewModel'
import { ProjectInfo, ProjectTimeZone, LookupItem } from '../types/Project/ProjectInfo'
import { ProjectFeature } from '../types/Project/ProjectFeature'
import {
	getOne,
	getAll,
	post,
	postWithResponse,
	put,
	deleteOne,
	patch,
	deleteMany,
} from './CommonServices/httpService'
import { AddWITTRequest } from '../components/project/edit/AddWorkItemTaskTemplate'
import { useTokenClaims } from '../security/useTokenClaims'
import { JobRole } from '../types/Employee/JobRole'
import { WorkArea } from '../types/Project/WorkArea'
import { ComponentType, ProjectComponentTypes } from '../types/Project/ComponentType'
import { WorkItemMetadata } from '../types/Project/WorkItemMetadata'
import { Spec } from '../types/Project/Spec'
import { SmallWorker } from '../types/Employee/Employee'
import { WorkerMetadata } from '../types/Project/WorkerMetadata'
import { NonProgressibleWorkType, PatchNonProgressiblesRequest } from '../types/Work/NonProgressible'
import { BatchRequest, BatchResponse } from '../types/Core/Batch'

const getProjectInfo = async (): Promise<ProjectInfo> => {
	const { projectId } = useTokenClaims()
	const result = await getOne<ProjectInfo>(`/Projects/${projectId}`, 'Project Info failed to load')
	return result.data
}

const getProjectAreas = async (): Promise<WorkArea[]> => {
	const { projectId } = useTokenClaims()
	const result = await getAll<WorkArea[]>(
		`/Projects/${projectId}/Areas`,
		'Project Areas failed to load',
	)
	return result.data
}

const getAssignedProjects = async (): Promise<ProjectInfo[]> => {
	const result = await getOne<ProjectInfo[]>(`/Projects`, '')
	return result.data
}

const getProjectRoles = async (projectId: number): Promise<JobRole[]> => {
	const result = await getAll<JobRole[]>(`/Projects/${projectId}/Roles`)
	return result.data
}

const getAvailableCrafts = async (): Promise<Craft[]> => {
	const result = await getAll<Craft[]>(`/Crafts`)
	return result.data
}

const createProject = async (rq: ProjectViewModel) => {
	const rqMapped = {
		title: rq.name,
		address: rq.address,
		projectNumber: rq.projectNumber,
		customer: rq.customer,
		startDate: rq.start ? dayjs(rq.start).toJSON() : undefined,
		endDate: rq.end ? dayjs(rq.end).toJSON() : undefined,
		type: rq.type,
		template: rq.template,
		crafts: rq.crafts,
		roles: rq.roles,
		timeZone: rq.timeZone,
		erp: rq.erp,
		regionId: rq.regionId,
		regionalOfficeId: rq.regionalOfficeId,
	}
	return await postWithResponse<number>('/projects', rqMapped)
}

const updateProject = async (rq: ProjectViewModel) => {
	const rqMapped = {
		title: rq.name,
		address: rq.address,
		projectNumber: rq.projectNumber,
		customer: rq.customer,
		startDate: rq.start ? dayjs(rq.start).toJSON() : undefined,
		endDate: rq.end ? dayjs(rq.end).toJSON() : undefined,
		type: rq.type,
		crafts: rq.crafts,
		roles: rq.roles,
	}
	return await put(`/projects/${rq.id}`, rqMapped)
}

const deleteProject = async () => {
	const { projectId } = useTokenClaims()
	return await deleteOne(`/projects/${projectId}`)
}

const activateProject = async () => {
	const { projectId } = useTokenClaims()
	return await patch(`/projects/${projectId}/Activate`, {})
}

const addProjectMetadata = async (index: number, name: string, type: string) => {
	const { projectId } = useTokenClaims()
	return await post(`/projects/${projectId}/MetaData`, { index, name, type })
}

const addProjectWBSItem = async (name: string, isOutOfScope: boolean) => {
	const { projectId } = useTokenClaims()
	return await post(`/projects/${projectId}/WBS`, { name, isOutOfScope })
}

const addProjectWBSWorkItem = async (
	name: string,
	wbsId: number,
	type: 'Work Order' | 'Purchase Order' | 'Tag Number' | 'None',
) => {
	return await post(`/WBS/${wbsId}/WorkItemTypes`, { name, type })
}

const deleteProjectMetadata = async (id: number) => {
	return await deleteOne(`/MetaData/${id}`)
}

const deleteWbsItem = async (id: number) => {
	return await deleteOne(`/WBS/${id}`)
}

const deleteWbsWorkItem = async (wbsId: number, workItemId: number) => {
	return await deleteOne(`/WBS/${wbsId}/Types/${workItemId}`)
}

const deleteSpecItem = async (id: number) => {
	return await deleteOne(`/Specs/${id}`)
}

const deleteWITT = async (specId: number, wittId: number) => {
	return await deleteOne(`/Specs/${specId}/Tasks/${wittId}`)
}

const addProjectSpecItem = async (name: string, craftId: number) => {
	const { projectId } = useTokenClaims()
	return await post(`/projects/${projectId}/Specs`, { name, craftId })
}

const addSpecWITT = async (specId: number, data: AddWITTRequest) => {
	return await post(`/specs/${specId}/Tasks`, {
		creditRule: data.creditRule,
		itpRequirement: data.itpRequirement,
		name: data.taskName,
		isQC: data.isQC,
		activityCode: data.activityCode,
	})
}

const deleteArea = async (areaId: number) => {
	return await deleteOne(`/Projects/Area/${areaId}`)
}

const addArea = async (areas: string[]) => {
	return await post(`/Projects/Areas`, areas)
}

const updateArea = async (area: WorkArea) => {
	return await patch(`/Projects/Area`, area)
}

const getProjectFeatures = async (): Promise<ProjectFeature[]> => {
	const result = await getAll<ProjectFeature[]>(
		`/Projects/Features`,
		'Project Features failed to load',
	)
	return result.data
}

const enableFeature = async (featureId: number): Promise<void> => {
	await post(`/Projects/Features/${featureId}/Enable`, {})
}

const disableFeature = async (featureIds: number[]): Promise<void> => {
	await deleteMany(`/Projects/Features`, featureIds)
}

const getProjectTimeZone = async (): Promise<ProjectTimeZone> => {
	const result = await getOne<{ timeZone: ProjectTimeZone }>(`/Projects/TimeZone`)
	return result.data.timeZone
}

const saveConsent = async (dayStartPrompt: string, dayEndPrompt: string): Promise<void> => {
	await post(`/Projects/Consent`, { dayStartPrompt, dayEndPrompt })
}

const getConsent = async (): Promise<{ dayStartPrompt: string; dayEndPrompt: string }> => {
	const result = await getOne<{ dayStartPrompt: string; dayEndPrompt: string }>(`/Projects/Consent`)
	return result.data
}

const getProjectComponentTypes = async (): Promise<ProjectComponentTypes[]> => {
	const result = await getAll<ProjectComponentTypes[]>(`/WBS/ComponentTypes`)
	return result.data
}

const addProjectComponentType = async (craftId: number, wbsId: number, types: ComponentType[]) => {
	return await post(`/WBS/${wbsId}/ComponentTypes/Craft/${craftId}`, types)
}

const deleteProjectComponentType = async (typeId: number) => {
	return await deleteOne(`/WBS/ComponentType/${typeId}`)
}

const updateProjectComponentType = async (takeOff: ProjectComponentTypes) => {
	return await put(`/WBS/ComponentType`, takeOff)
}

const getProjectWorkItemMetadata = async () => {
	const result = await getAll<WorkItemMetadata[]>(`/Projects/MetaData/WorkItem`)
	return result.data
}

const getProjectSpecs = async (includeWorkItemTaskTemplates = false) => {
	const result = await getAll<Spec[]>(
		`/Projects/Specs?includeWorkItemTaskTemplates=${includeWorkItemTaskTemplates}`,
	)
	return result.data
}

const getProjectWorkerMetadata = async () => {
	const result = await getAll<WorkerMetadata[]>(`/Projects/MetaData/Worker`)
	return result.data
}

const lookupProjectMembers = async (searchTerm: string) => {
	return await getAll<SmallWorker[]>(`/Workers/Lookup?searchText=${searchTerm}`)
}

const listProjectControls = async () => {
	return await getAll<SmallWorker[]>(`/Projects/ProjectControls`)
}

const addProjectControls = async (
	firstName: string,
	lastName: string,
	employeeId: string,
	email: string,
) => {
	return await post(`/Projects/ProjectControls`, { firstName, lastName, employeeId, email })
}

const removeProjectControls = async (id: number) => {
	return await deleteOne(`/Projects/ProjectControls/${id}`)
}

const listOfficeAdmins = async () => {
	return await getAll<SmallWorker[]>(`/Projects/OfficeAdmins`)
}

const addOfficeAdmin = async (
	firstName: string,
	lastName: string,
	employeeId: string,
	email: string,
) => {
	return await post(`/Projects/OfficeAdmins`, { firstName, lastName, employeeId, email })
}

const removeOfficeAdmin = async (id: number) => {
	return await deleteOne(`/Projects/OfficeAdmins/${id}`)
}

const getProjectCrafts = async (): Promise<Craft[]> => {
	const result = await getAll<Craft[]>(`/Crafts/Project`)
	return result.data
}

const patchNonProgressibleWorkTypes = async (request: PatchNonProgressiblesRequest) => {
	return await patch(`/Projects/NonProgressible`, request)
}

const getNonProgressibleWorkTypes = async (
	extended: boolean,
	batchRequest: BatchRequest,
): Promise<BatchResponse<NonProgressibleWorkType>> => {
	const params = new URLSearchParams({
		extended: String(extended),
		batchSize: String(batchRequest.batchSize),
		batchNumber: String(batchRequest.batchNumber),
	})
	const term = batchRequest.searchTerm?.trim()
	if (term) {
		params.set('searchTerm', term)
	}
	const result = await getAll<BatchResponse<NonProgressibleWorkType>>(
		`/Projects/NonProgressible?${params.toString()}`,
	)
	return result.data
}

const getProjectTagStart = async (): Promise<number> => {
	const result = await getOne<number>(`/Projects/TagStart`)
	return result.data
}

const updateProjectTagStart = async (tagStart: number): Promise<void> => {
	await patch(`/Projects/TagStart/${tagStart}`, {})
}

// TEMP: Legacy unpaid-break backfill — remove this type and function when the one-shot admin tool is no longer needed
export type BackfillUnpaidBreaksResult = {
	timesheetsAllocated: number
	timeRecordsAllocated: number
	tasksUpdated: number
	reworksUpdated: number
	workItemsUpdated: number
}

const backfillUnpaidBreaks = async (): Promise<BackfillUnpaidBreaksResult> => {
	const result = await postWithResponse<BackfillUnpaidBreaksResult>(
		`/Projects/BackfillUnpaidBreaks`,
		{},
	)
	return result.data
}

// TEMP: Parent hours-progress backfill — remove this type and function when the one-shot admin tool is no longer needed
export type BackfillParentWorkItemProgressResult = {
	workItemsUpdated: number
}

const backfillParentWorkItemProgress = async (): Promise<BackfillParentWorkItemProgressResult> => {
	const result = await postWithResponse<BackfillParentWorkItemProgressResult>(
		`/Projects/BackfillParentWorkItemProgress`,
		{},
	)
	return result.data
}

const getProjectTemplate = async (): Promise<number> => {
	const result = await getOne<number>(`/Projects/Template`)
	return result.data
}

const getRegions = async (): Promise<LookupItem[]> => {
	const result = await getAll<LookupItem[]>(`/Projects/Regions`)
	return result.data
}

const getRegionalOffices = async (): Promise<LookupItem[]> => {
	const result = await getAll<LookupItem[]>(`/Projects/RegionalOffices`)
	return result.data
}

export {
	getProjectInfo,
	getAssignedProjects,
	getAvailableCrafts,
	getProjectRoles,
	createProject,
	updateProject,
	activateProject,
	addProjectMetadata,
	deleteProjectMetadata,
	addProjectWBSItem,
	addProjectWBSWorkItem,
	addProjectSpecItem,
	addSpecWITT,
	deleteWbsItem,
	deleteWbsWorkItem,
	deleteSpecItem,
	deleteWITT,
	deleteProject,
	getProjectAreas,
	deleteArea,
	addArea,
	updateArea,
	getProjectFeatures,
	enableFeature,
	disableFeature,
	getProjectTimeZone,
	saveConsent,
	getConsent,
	getProjectComponentTypes,
	addProjectComponentType,
	deleteProjectComponentType,
	updateProjectComponentType,
	getProjectWorkItemMetadata,
	getProjectSpecs,
	lookupProjectMembers,
	listProjectControls,
	addProjectControls,
	removeProjectControls,
	listOfficeAdmins,
	addOfficeAdmin,
	removeOfficeAdmin,
	getProjectWorkerMetadata,
	getProjectCrafts,
	patchNonProgressibleWorkTypes,
	getNonProgressibleWorkTypes,
	getProjectTagStart,
	updateProjectTagStart,
	backfillUnpaidBreaks, // TEMP: Legacy unpaid-break backfill
	backfillParentWorkItemProgress, // TEMP: Parent hours-progress backfill
	getProjectTemplate,
	getRegions,
	getRegionalOffices,
}
